import {
  PackingGroupDto,
  PackingGroupDetailDto,
  ShipmentDetailDto,
  CreateShipmentRequest,
  UpdateShipmentRequest,
  ShipmentOperationResponse,
  OrderLineDto,
  DeliveryMethodDto,
  PackZoneDto,
  LoadingZoneDto,
  PackageConfirmationStatusDto,
  ConfirmPackageRequest,
  ConfirmPackageResponse,
  CreateConsolidationShipmentRequest
} from '../types/packing';
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class PackingService {
  /**
   * Get all packing groups optionally filtered by pack zone
   */
  async getPackingGroups(packZoneId?: string): Promise<PackingGroupDto[]> {
    const url = packZoneId
      ? `${API_BASE_URL}/packing/groups?packZoneId=${encodeURIComponent(packZoneId)}`
      : `${API_BASE_URL}/packing/groups`;

    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch packing groups: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get detailed information for a specific packing group
   */
  async getPackingGroupDetails(groupingId: string): Promise<PackingGroupDetailDto> {
    const url = `${API_BASE_URL}/packing/groups/detail?groupingId=${encodeURIComponent(groupingId)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch packing group details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get or create shipment for a grouping
   */
  async getOrCreateShipment(groupingId: string): Promise<ShipmentDetailDto> {
    const url = `${API_BASE_URL}/packing/shipment?groupingId=${encodeURIComponent(groupingId)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to get shipment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a new shipment for a grouping
   */
  async createShipment(request: CreateShipmentRequest): Promise<ShipmentOperationResponse> {
    const url = `${API_BASE_URL}/packing/shipment`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      throw new Error(`Failed to create shipment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update an existing shipment
   */
  async updateShipment(request: UpdateShipmentRequest): Promise<ShipmentOperationResponse> {
    const url = `${API_BASE_URL}/packing/shipment/${request.shipmentID}`;
    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      throw new Error(`Failed to update shipment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Finalize/complete a shipment
   */
  async finalizeShipment(shipmentID: number): Promise<ShipmentOperationResponse> {
    const url = `${API_BASE_URL}/packing/shipment/${shipmentID}/finalize`;
    const response = await AuthService.authenticatedPost(url, {});

    if (!response.ok) {
      // 400 fra API-et bærer ShipmentOperationResponse med forklarende melding
      // (f.eks. «plukk pågår fortsatt») — statusText er tom i HTTP/2.
      const body = await response.json().catch(() => null);
      if (body && typeof body.message === 'string' && body.message) {
        return body as ShipmentOperationResponse;
      }
      throw new Error(`Failed to finalize shipment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get order lines for a specific order (for drill-down view).
   * When pListId is supplied, returns only the lines for that picking list
   * (required when an order is split across multiple PLists).
   */
  async getOrderLines(orderNo: number, pListId?: number): Promise<OrderLineDto[]> {
    const url = pListId !== undefined
      ? `${API_BASE_URL}/packing/orders/${orderNo}/lines?pListId=${pListId}`
      : `${API_BASE_URL}/packing/orders/${orderNo}/lines`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch order lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get delivery methods for dropdown
   */
  async getDeliveryMethods(): Promise<DeliveryMethodDto[]> {
    const url = `${API_BASE_URL}/packing/deliverymethods`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch delivery methods: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get pack zones for dropdown
   */
  async getPackZones(): Promise<PackZoneDto[]> {
    const url = `${API_BASE_URL}/packing/packzones`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch pack zones: ${response.statusText}`);
    }

    return response.json();
  }

  async getLoadingZones(): Promise<LoadingZoneDto[]> {
    const url = `${API_BASE_URL}/packing/loadingzones`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch loading zones: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get package counts from PListOrderPackages grouped by OrderNo for a given PList.
   * Used to display accurate kolli count per order when sending to packing.
   */
  async getPackageCountsByPList(pListId: number): Promise<Record<number, number>> {
    const url = `${API_BASE_URL}/packing/plist/${pListId}/package-counts`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch package counts: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Slett alle kolli (PListOrderPackages) for et plukk. Backend tillater kun sletting
   * når ingen linjer er plukket — brukes fra «Lås opp» i Mine plukk.
   */
  async deletePackagesByPList(pListId: number): Promise<{ deleted: number }> {
    const url = `${API_BASE_URL}/packing/plist/${pListId}/packages`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      throw new Error(`Failed to delete packages: ${response.statusText}`);
    }

    return response.json();
  }

  // ==================== PACKAGE CONFIRMATION METHODS ====================

  /**
   * Get package confirmation status for a shipment/grouping.
   * Returns all packages that need to be confirmed before finalization.
   */
  async getPackageConfirmationStatus(groupingId: string): Promise<PackageConfirmationStatusDto> {
    const url = `${API_BASE_URL}/packing/packages?groupingId=${encodeURIComponent(groupingId)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch package confirmation status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Confirm a package by scanning.
   * Validates the scanned package ID and updates confirmation status.
   */
  async confirmPackage(request: ConfirmPackageRequest): Promise<ConfirmPackageResponse> {
    const url = `${API_BASE_URL}/packing/packages/confirm`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      return {
        success: false,
        message: extractApiError(errorData, response, 'Feil ved bekreftelse'),
        confirmedPackage: null,
        nextPackage: null,
        status: null
      };
    }

    return response.json();
  }

  /**
   * Check if package checkout is required from Profile settings.
   */
  async isPackageCheckoutRequired(): Promise<boolean> {
    const url = `${API_BASE_URL}/packing/config/packagecheckout`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      console.warn('Failed to check package checkout requirement, defaulting to false');
      return false;
    }

    return response.json();
  }

  // ==================== CONSOLIDATION SHIPMENT METHODS ====================

  /**
   * Create a consolidation shipment for multiple orders from a single picking list.
   * Used when completing direct picking with "samlasting" (consolidation) option.
   * Creates a Shipment entity and links all PListOrders to it via ShipmentID,
   * enabling Cargonizer to retrieve all ShipmentLines for the consolidated shipment.
   */
  async createConsolidationShipment(request: CreateConsolidationShipmentRequest): Promise<ShipmentOperationResponse> {
    const url = `${API_BASE_URL}/packing/shipment/consolidation`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create consolidation shipment'));
    }

    return response.json();
  }

  /**
   * Get status badge color based on completion status
   */
  getStatusBadgeColor(isComplete: boolean): string {
    return isComplete
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  }

  /**
   * Get status badge color based on group status
   */
  getGroupStatusColor(groupStatus: string): string {
    switch (groupStatus) {
      case 'Complete':
        // Ferdig pakket
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Ready':
        // Alle plukk ferdige — klar til å pakke (handlingsbar tilstand operatøren venter på)
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'InProgress':
        // Plukking pågår
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Waiting':
        // Venter på plukk
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Mixed':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }

  /**
   * Format order display text (legacy format: "OrderNo (split/total) (packages) Operator")
   */
  formatOrderDisplayText(
    orderNoERP: string | null,
    pickZoneSplitNum: number,
    numOfPickZoneSplits: number,
    numOfPackages: number,
    assignedTo: string | null
  ): string {
    const orderNo = orderNoERP || '-';
    const splits = numOfPickZoneSplits > 0
      ? `(${pickZoneSplitNum}/${numOfPickZoneSplits})`
      : '(1/1)';
    const packages = `(${numOfPackages})`;
    const operator = assignedTo ? ` ${assignedTo}` : '';

    return `${orderNo} ${splits} ${packages}${operator}`;
  }
}

export const packingService = new PackingService();
